import Card from "../UI/Card";
import useAccount from "../../hooks/use-account";
import Button from "../UI/Button";
import { useModal } from "../../hooks/use-modal.jsx";
import ChangePasswordForm from "./ChangePasswordForm";
import ChangeEmailForm from "./ChangeEmailForm.tsx";
import moment from "moment/moment";
import { CalendarCheck, Interrogation } from "../UI/Icons";
import Tooltip from "../UI/Tooltip";
import BuyCredits from "../Stripe/BuyCredits";
import BuySubscriptionForm from "../Stripe/BuySubscriptionForm";
import { classnames } from "../../utils";
import styles from "./MonCompte.module.css";
import { useState } from "react";
import { useStripeAbonnementPrice } from "../../hooks/use-stripe-prices";
import { TypeAbonnement } from "../../utils/constants/account";
import { getAbonnementDetails } from "../../utils/abonnement";
import Spinner from "../UI/Spinner";
import CreditsCard from "./CreditsCard";
import IconAndText from "../UI/IconAndText";
import useMediaQuery from "../../hooks/use-media-query";
import { currencyFormatter } from "../../api/Functions";
import BuyComptabiliteForm from "../Stripe/BuyComptabiliteForm";
import DeleteAccountForm from "./DeleteAccountForm";
import TwoFactorAuthEnableForm from "./TwoFactorAuthEnableForm.jsx";
import TwoFactorAuthDisableForm from "./TwoFactorAuthDisableForm.jsx";
import MCPTokensList from "./MCPTokensList.tsx";
import UnsubscribeModal from "./UnsubscribeModal";
import { useLocation } from "react-router-dom";
import { conversionData, trackEvent } from "../../utils/tracking";
import { trackMetaCustomEvent } from "../../utils/metaPixel";

export default function MonCompte() {
  const {
    account,
    refresh: refreshAccount,
    changePassword,
    unsubscribe,
    deleteAccount,
    isLoading,
  } = useAccount();

  const nbBiens = account.nb_biens_abonnement;
  const nbBiensApresExpiration = account.nb_biens_abonnement_apres_expiration;
  const { price } = useStripeAbonnementPrice(
    account.stripe_price_abonnement,
    nbBiens,
  );
  const { price: priceApresExpiration } = useStripeAbonnementPrice(
    account.stripe_price_abonnement_apres_expiration,
    nbBiensApresExpiration,
  );
  const [fiscalYear, setFiscalYear] = useState(null);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [ChangePassword, closeChangePassword, openChangePassword] = useModal();
  const [TwoFactorEnableModal, closeTwoFactorEnable, openTwoFactorEnable] =
    useModal();
  const [TwoFactorDisableModal, closeTwoFactorDisable, openTwoFactorDisable] =
    useModal();

  const [AccountBuyCredits, , openAccountBuyCredits] = useModal();
  const [SubscribeModal, closeSubscribeModal, openSubscribeModal] = useModal();
  const [DeleteModal, , openDeleteModal] = useModal();
  const [ComptabiliteModal, closeCompatibiliteModal, openComptabiliteModal] =
    useModal();
  const [
    UnsubscribeModalComponent,
    closeUnsubscribeModal,
    openUnsubscribeModal,
  ] = useModal();
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const location = useLocation();

  async function handleUnsubscribe(values) {
    const result = await unsubscribe(values);
    const conversion = conversionData(result);
    trackEvent("unsubscribed", location, {
      ...conversion,
      reason: values.motif,
      user_data: { email: account.mail },
    });
    trackMetaCustomEvent("Unsubscribe", conversion.transaction_id, {
      value: conversion.value,
      currency: conversion.currency,
      reason: values.motif,
    });
    closeUnsubscribeModal();
  }

  const isAbonne =
    (account.expiration_abonnement === null ||
      moment(account.expiration_abonnement).isAfter(moment())) &&
    (![TypeAbonnement.AUCUN, TypeAbonnement.TRIAL].includes(
      account.type_abonnement,
    ) ||
      (account.type_abonnement === TypeAbonnement.TRIAL &&
        account.autorenew_abonnement));

  const { name, desc, recurrence, by, rateInt, rateDec } = getAbonnementDetails(
    price,
    isAbonne || account.type_abonnement === TypeAbonnement.TRIAL
      ? account.type_abonnement
      : TypeAbonnement.AUCUN,
    nbBiens,
    account.promos,
  );

  const {
    name: nameApresExpiration,
    desc: descApresExpiration,
    rate: rateApresExpiration,
  } = getAbonnementDetails(
    priceApresExpiration,
    account.type_abonnement_apres_expiration,
    nbBiensApresExpiration,
    account.promos,
  );

  const today = moment();
  let fiscalYears = [today.year() - 1, today.year()];
  if (today > moment(`${today.year()}-05-31`)) {
    fiscalYears = [today.year()];
  }

  return (
    <>
      <DeleteModal>
        <DeleteAccountForm onSubmit={deleteAccount} isLoading={isLoading} />
      </DeleteModal>
      <ChangeEmailForm
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
      />
      <ChangePassword>
        <ChangePasswordForm
          onSubmit={async (values) => {
            await changePassword(values);
            closeChangePassword();
          }}
        />
      </ChangePassword>
      <TwoFactorEnableModal size={"medium"}>
        <TwoFactorAuthEnableForm onClose={closeTwoFactorEnable} />
      </TwoFactorEnableModal>
      <TwoFactorDisableModal>
        <TwoFactorAuthDisableForm onClose={closeTwoFactorDisable} />
      </TwoFactorDisableModal>
      <AccountBuyCredits size={"medium"}>
        <BuyCredits
          account={account}
          refreshAccount={refreshAccount}
          buyCredit
        />
      </AccountBuyCredits>
      <BuySubscriptionForm
        onSubscribed={closeSubscribeModal}
        Modal={SubscribeModal}
      />
      <BuyComptabiliteForm
        Modal={ComptabiliteModal}
        fiscalYear={fiscalYear}
        onPaymentSuccessful={closeCompatibiliteModal}
      />
      <UnsubscribeModalComponent header="Annuler l'abonnement" size="medium">
        <UnsubscribeModal onSubmit={handleUnsubscribe} />
      </UnsubscribeModalComponent>
      <div className={styles.wrapper}>
        <Card>
          <p className={"cardTitle marginB10"}>Informations personnelles</p>
          <div className={"flex gap10 align-items-center"}>
            {!isSmallScreen && (
              <img src={"/photo_mon_compte.jpg"} alt="mon-compte" />
            )}
            <div>
              <p className={"secondaryText"}>Adresse e-mail de connexion</p>
              <p className={"primaryText"}>{account.mail}</p>
              <p className={"secondaryText marginT10"}>2FA</p>
              <p className={"primaryText"}>
                {account.totp_enabled ? "Activé" : "Désactivé"}
              </p>
              {account.telephone && (
                <>
                  <p className={"secondaryText marginT10"}>
                    Numéro de téléphone
                  </p>
                  <p className={"primaryText"}>{account.telephone}</p>
                </>
              )}
            </div>
          </div>
          <div className={styles.buttonRow}>
            <Button
              variant="secondary"
              onClick={() => setChangeEmailOpen(true)}
            >
              Modifier l'e-mail
            </Button>
            <Button variant="secondary" onClick={openChangePassword}>
              Modifier le mot de passe
            </Button>
            {account.totp_enabled ? (
              <Button variant="secondary" onClick={openTwoFactorDisable}>
                Désactiver le 2FA
              </Button>
            ) : (
              <Button variant="secondary" onClick={openTwoFactorEnable}>
                Activer le 2FA
              </Button>
            )}
          </div>
        </Card>
        {/* Comptes pro : pas de gestion d'abonnement en self-service pour l'instant. */}
        {account.home !== "/pro" && (
          <Card>
            <p className={"cardTitle"}>Votre abonnement</p>
            <div className={"marginT10 flex space-between align-items-end"}>
              {name && (
                <>
                  <div>
                    <p className={styles.subscriptionDesc}>
                      Formule {name} <br />
                      {desc}
                    </p>
                    <IconAndText
                      text={
                        <>
                          <p>
                            {account.expiration_abonnement &&
                            account.autorenew_abonnement
                              ? "Prochaine échéance"
                              : "Date d'expiration"}{" "}
                            :{" "}
                            {moment(account.expiration_abonnement) > moment()
                              ? moment(account.expiration_abonnement).format(
                                  "DD/MM/YYYY",
                                )
                              : "jamais"}
                          </p>
                          <p>
                            {account.autorenew_abonnement &&
                              nameApresExpiration && (
                                <>
                                  Puis : Formule {nameApresExpiration} -{" "}
                                  {descApresExpiration} - {rateApresExpiration}
                                </>
                              )}
                          </p>
                        </>
                      }
                      icon={<CalendarCheck stroke={"#9D9D9D"} />}
                      className={"marginT10"}
                    />
                  </div>

                  <div>
                    <p className={"primaryBlue text-center marginB5"}>
                      Abonnement {recurrence}
                    </p>
                    <Card
                      className={classnames(
                        "text-center",
                        "bg-blue",
                        styles.price,
                      )}
                      padding={"md"}
                    >
                      {account.type_abonnement !== TypeAbonnement.AUCUN &&
                      account.type_abonnement !== TypeAbonnement.TRIAL ? (
                        <p className={styles.subscriptionPrice}>
                          <span className={styles.priceSymbol}>
                            € <span className={styles.priceInt}>{rateInt}</span>
                          </span>
                          <span className={styles.priceDec}>
                            {rateDec} TTC <br />
                            {by}
                          </span>
                        </p>
                      ) : account.type_abonnement === TypeAbonnement.TRIAL ? (
                        "Essai gratuit"
                      ) : (
                        "Gratuit"
                      )}
                    </Card>
                  </div>
                </>
              )}
              {!name && <p className="primaryText">Aucun</p>}
            </div>
            <div
              className={"marginT10 flex space-between marginT20 wrap gap10"}
            >
              {name && (
                <Button
                  variant="secondary"
                  disabled={!account.autorenew_abonnement}
                  onClick={openUnsubscribeModal}
                >
                  Annuler l'abonnement
                </Button>
              )}
              {price?.type_abonnement !== TypeAbonnement.CUSTOM && (
                <Button varian="secondary" onClick={openSubscribeModal}>
                  {isAbonne ? "Changer d'abonnement" : "S'abonner"}
                </Button>
              )}
            </div>
          </Card>
        )}
        {account.home === "/bailleur" && (
          <Card>
            <p className={"cardTitle"}>Abonnement comptabilité</p>
            <div
              className={
                "flex wrap gap10 align-items-center marginT5 justify-content-center"
              }
            >
              <div className={"text-center"}>
                <p className={"marginB10 primaryText"}>
                  Souscrivez à l'offre comptabilité de votre choix
                </p>
                {fiscalYears.map((year) => (
                  <div key={year}>
                    <Button
                      className={"marginB10"}
                      onClick={() => {
                        setFiscalYear(year);
                        openComptabiliteModal();
                      }}
                    >
                      {year + 1} sur les revenus {year}
                    </Button>{" "}
                  </div>
                ))}
                <div className={"marginT10"}>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      window.open(
                        "https://www.qalimo.fr/comptabilite-lmnp/",
                        "_blank",
                      )
                    }
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
        <Card>
          <p className={"cardTitle marginB20"}>
            Vos crédits disponibles{" "}
            <Tooltip
              content={
                "Utilisés pour les signatures électroniques et lettres recommandées"
              }
            >
              <Interrogation />
            </Tooltip>
          </p>
          <CreditsCard
            credits={account.credits / 100}
            className="marginAuto marginB20 padding20"
          />
          <div className={"text-center"}>
            <Button
              variant="tertiary"
              className={"marginL10"}
              onClick={openAccountBuyCredits}
            >
              Acheter des crédits
            </Button>
          </div>
        </Card>
        {(account.home === "/bailleur" || account.home === "/pro") && (
          <MCPTokensList />
        )}
        {account.home !== "/pro" && (
          <Card>
            <div className={"flex space-between gap10 wrap"}>
              <p className={"cardTitle"}>Suppression du compte</p>
              <div className={"text-center"}>
                <p className={"primaryBlue marginT20 marginB20"}>
                  ATTENTION : CETTE OPERATION EST IMMEDIATE ET DEFINITIVE
                </p>
                <Button variant="primary" onClick={openDeleteModal}>
                  Supprimer votre compte
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

export function AbonnementType({
  type,
  nbBiens,
  stripePriceId,
  montantReduit,
}) {
  const { price, isLoading } = useStripeAbonnementPrice(stripePriceId, nbBiens);
  if (isLoading) return <Spinner />;
  const { rate, name, desc } = getAbonnementDetails(price, type, nbBiens);
  if (!name) return "Aucun abonnement";
  let fullRate = rate;
  if (montantReduit) {
    fullRate = `${currencyFormatter(montantReduit)} puis ${fullRate}`;
  }
  return `${name} - ${desc} - ${fullRate}`;
}
